import React, { useEffect, useState } from "react";
import { Alert, Button, Modal, Radio, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { LANGUAGE_OPTIONS } from "../../Language/languageOptions";
import { downloadInvoice } from "../../../utils/downloadInvoice";
import { Error, Warning } from "../../Messages/Messages";
import "./InvoiceDialogs.css";

const STORAGE_KEY = "invoicePrintLanguage";

// German first: it is the original document, the others are translated prints
const PRINT_LANGUAGES = [
  ...LANGUAGE_OPTIONS.filter(l => l.code === "de"),
  ...LANGUAGE_OPTIONS.filter(l => l.code !== "de")
];

const lastLanguage = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return PRINT_LANGUAGES.some(l => l.code === stored) ? stored : "de";
  } catch {
    return "de";
  }
};

/**
 * Print / download an invoice PDF in a chosen language. German is the archived original (with
 * the embedded e-invoice); other languages are translated prints made on demand.
 */
const PrintDialog = ({ open, invoiceId, fileName, onClose }) => {
  const { t: ta } = useTranslation("accounting");
  const [lang, setLang] = useState("de");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setLang(lastLanguage());
  }, [open]);

  const print = async () => {
    setBusy(true);
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      // the choice is only a convenience
    }
    try {
      const { translation } = await downloadInvoice(invoiceId, null, {
        lang,
        fileName: lang === "de" ? fileName : `${fileName}-${lang.toUpperCase()}`
      });
      if (translation === "partial")
        Warning(
          ta(
            "The item texts could not be translated right now and are printed in German. Please try again later."
          )
        );
      onClose();
    } catch {
      Error(ta("Could not download the PDF"));
    }
    setBusy(false);
  };

  return (
    <Modal
      rootClassName="invoice-dialog"
      title={ta("Print invoice")}
      open={open}
      width={440}
      onCancel={busy ? undefined : onClose}
      closable={!busy}
      maskClosable={!busy}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onClose} disabled={busy}>
          {ta("Cancel")}
        </Button>,
        <Button key="print" type="primary" loading={busy} onClick={print}>
          {ta("Print")}
        </Button>
      ]}
    >
      <div className="invoice-dialog__body">
        <Typography.Text strong>{ta("Language of the invoice")}</Typography.Text>
        <Radio.Group
          className="invoice-dialog__languages"
          value={lang}
          onChange={e => setLang(e.target.value)}
          disabled={busy}
        >
          {PRINT_LANGUAGES.map(l => (
            <Radio key={l.code} value={l.code}>
              <span lang={l.code}>{l.name}</span>
              {l.code === "de" ? (
                <Typography.Text type="secondary"> · {ta("Original document")}</Typography.Text>
              ) : null}
            </Radio>
          ))}
        </Radio.Group>
        {lang !== "de" ? (
          <Alert
            type="info"
            showIcon
            message={ta(
              "All labels and legal notes are translated, and item descriptions and your texts are translated automatically. The German invoice stays the original document."
            )}
          />
        ) : null}
        {busy && lang !== "de" ? (
          <Typography.Text type="secondary">
            {ta("Translating the invoice… this can take up to a minute.")}
          </Typography.Text>
        ) : null}
      </div>
    </Modal>
  );
};

export default PrintDialog;
